/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { SKELETON_BLOCK } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';

/** One full row, then two pairs — the same grid the card form uses. */
const PAIR: { label: string; input: string }[] = [
  { label: 'w-1/2', input: 'w-full' },
  { label: 'w-2/5', input: 'w-full' },
];

/**
 * Placeholder shaped like Payien's hosted card form.
 *
 * Stands in for the iframe across both waits the user can't tell apart —
 * the session being minted and the framed page loading — so the modal never
 * opens onto an empty box. Its height matches the iframe's `minHeight` so
 * the hand-off doesn't shift the dialog.
 *
 * @returns The shimmering card-form placeholder.
 */
export const PaymentFormSkeleton: React.FC = () => {
  const { t } = useTranslation('features/pricing');

  return (
    <div
      data-testid="payment-form-skeleton"
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex flex-col gap-(--space-4)"
    >
      <Text component="span" className="sr-only">{t('subscription.payment.loadingForm')}</Text>
      <div className="flex flex-col gap-(--space-2)">
        <div className={`${SKELETON_BLOCK} h-3 w-1/3`} />
        <div className={`${SKELETON_BLOCK} h-10 w-full`} />
      </div>
      {[0, 1].map((row) => (
        <div key={row} className="grid grid-cols-2 gap-(--space-3)">
          {PAIR.map((field) => (
            <div key={`${row}-${field.label}`} className="flex flex-col gap-(--space-2)">
              <div className={`${SKELETON_BLOCK} h-3 ${field.label}`} />
              <div className={`${SKELETON_BLOCK} h-10 ${field.input}`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default PaymentFormSkeleton;
